import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

type Me =
  | { authenticated: false }
  | { authenticated: true; id: string; email: string; firstName?: string; lastName?: string };
type CookieProbeResult = { hasCookie: boolean };

export default class SessionService extends Service {
  //private apiBase = "https://observation-eye-honors-expanding.trycloudflare.com"
  private apiBase = 'https://bbmo-auth.singularsound.workers.dev';

  @tracked user: Me = { authenticated: false };
  @tracked showCookieWarning = false;
  @tracked thirdPartyCookiesBlocked: boolean | null = null;

  private cookieCheckPromise: Promise<boolean> | null = null;
  private _pendingRedirect: string | null = null;
  private _pendingMode: 'login' | 'switch' | null = null;

  get nameOrEmail() {
    if (this.user.firstName) {
      return this.user.firstName;
    } else {
      return this.user.email;
    }
  }

  get isAuthenticated() {
    return this.user.authenticated === true;
  }

  get loginUrl() {
    return `${this.apiBase}/login`;
  }

  loginHref = (redirectTo: string) =>
    `${this.apiBase}/login?redirect=${encodeURIComponent(redirectTo)}`;

  switchHref = (redirectTo: string) =>
    `${this.apiBase}/switch-account?redirect=${encodeURIComponent(redirectTo)}`;

  loginClick = async (redirectTo: string, evt: MouseEvent) => {
    evt.preventDefault();
    const ok = await this.checkThirdPartyCookies();
    if (ok) {
      window.location.assign(this.loginHref(redirectTo));
    } else {
      this._pendingRedirect = redirectTo;
      this._pendingMode = 'login';
      this.showCookieWarning = true;
    }
  };

  switchClick = async (redirectTo: string, evt: MouseEvent) => {
    evt.preventDefault();
    const ok = await this.checkThirdPartyCookies();
    if (ok) {
      window.location.assign(this.switchHref(redirectTo));
    } else {
      this._pendingRedirect = redirectTo;
      this._pendingMode = 'switch';
      this.showCookieWarning = true;
    }
  };

  dismissCookieWarning = (): void => {
    this.showCookieWarning = false;
  };

  // When the user hits "Try again", re-probe and, if it's now ok, continue the nav
  retryCookieCheck = async () => {
    this.thirdPartyCookiesBlocked = null;
    const ok = await this.checkThirdPartyCookies();
    if (ok && this._pendingRedirect) {
      const to = this._pendingRedirect;
      const mode = this._pendingMode;
      this._pendingRedirect = null;
      this._pendingMode = null;
      this.showCookieWarning = false;
      window.location.assign(mode === 'switch' ? this.switchHref(to) : this.loginHref(to));
    }
  };

  async checkThirdPartyCookies(): Promise<boolean> {
    if (this.cookieCheckPromise) {
      return this.cookieCheckPromise;
    }

    const task = this.runCookieCheck();
    this.cookieCheckPromise = task;
    try {
      return await task;
    } finally {
      this.cookieCheckPromise = null;
    }
  }

  private async runCookieCheck(): Promise<boolean> {
    try {
      const first = await this.hitCookieProbe();
      if (first.hasCookie) {
        this.thirdPartyCookiesBlocked = false;
        this.showCookieWarning = false;
        return true;
      }

      // second check to see if the freshly set cookie sticks (blocked browsers will still be false)
      const second = await this.hitCookieProbe();
      const allowed = Boolean(second.hasCookie);
      this.thirdPartyCookiesBlocked = !allowed;
      this.showCookieWarning = !allowed;
      return allowed;
    } catch (e) {
      console.error('cookie probe failed', e);
      this.thirdPartyCookiesBlocked = null;
      this.showCookieWarning = false;
      return true;
    }
  }

  private async hitCookieProbe(): Promise<CookieProbeResult> {
    const res = await fetch(`${this.apiBase}/cookie-test`, {
      credentials: 'include',
      cache: 'no-store',
    });

    if (!res.ok) {
      throw new Error(`cookie probe failed (${res.status})`);
    }

    const data = (await res.json()) as CookieProbeResult;
    return { hasCookie: Boolean(data.hasCookie) };
  }

  async load(): Promise<void> {
    try {
      const res = await fetch(`${this.apiBase}/api/me`, {
        credentials: 'include',
        cache: 'no-store',
      });

      if (!res.ok) {
        this.user = { authenticated: false };
        return;
      }

      const data = (await res.json()) as Me;
      if (!data || data.authenticated !== true) {
        this.user = { authenticated: false };
        return;
      }

      this.user = data; // authenticated true path
    } catch {
      // Fail silently — just stay logged out
      this.user = { authenticated: false };
    }
  }

  logout = (): void => {
    const post = window.location.origin + "/";
    window.location.href = `${this.apiBase}/logout?post=${encodeURIComponent(post)}`;
    this.user = { authenticated: false };
  };
  }

// type registry
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
